import React, { useEffect } from 'react';

const PaymentNotification = ({ 
  type, 
  title, 
  message, 
  paymentId, 
  onClose, 
  autoClose = true,
  duration = 5000 
}) => {
  useEffect(() => {
    if (autoClose) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [autoClose, duration, onClose]);

  const getNotificationStyles = () => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-gradient-to-br from-green-100/90 via-emerald-50/95 to-green-100/90',
          border: 'border-green-300/40',
          backdrop: 'backdrop-blur-md bg-white/80',
          shadow: 'shadow-2xl shadow-green-500/25',
          icon: 'text-green-600',
          title: 'text-green-900',
          message: 'text-green-800'
        };
      case 'error':
        return {
          bg: 'bg-gradient-to-br from-red-100/90 via-pink-50/95 to-red-100/90',
          border: 'border-red-300/40',
          backdrop: 'backdrop-blur-md bg-white/80',
          shadow: 'shadow-2xl shadow-red-500/25',
          icon: 'text-red-600',
          title: 'text-red-900',
          message: 'text-red-800'
        };
      default:
        return {
          bg: 'bg-gradient-to-br from-blue-100/90 via-indigo-50/95 to-blue-100/90',
          border: 'border-blue-300/40',
          backdrop: 'backdrop-blur-md bg-white/80',
          shadow: 'shadow-2xl shadow-blue-500/25',
          icon: 'text-blue-600',
          title: 'text-blue-900',
          message: 'text-blue-800'
        };
    }
  };

  const styles = getNotificationStyles();

  const SuccessIcon = () => (
    <svg className={`w-6 h-6 sm:w-8 sm:h-8 ${styles.icon}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );

  const ErrorIcon = () => (
    <svg className={`w-6 h-6 sm:w-8 sm:h-8 ${styles.icon}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );

  const CloseIcon = () => (
    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );

  return (
    <div className="fixed inset-0 z-[9999] flex justify-center items-center animate-slide-in pointer-events-none">
      {/* Semi-transparent backdrop */}
      <div className="absolute inset-0 bg-black/20 backdrop-blur-sm pointer-events-auto" onClick={onClose}></div>
      
      {/* Notification container */}
      <div className="relative w-full max-w-xs sm:max-w-sm pointer-events-auto mx-4" style={{minWidth: '280px', maxWidth: '400px'}}>
        <div className={`${styles.bg} ${styles.border} ${styles.shadow} border-2 rounded-xl sm:rounded-2xl p-4 sm:p-6 ${styles.backdrop}`}>
        <div className="flex items-start space-x-2 sm:space-x-3">
          <div className="flex-shrink-0 mt-0.5">
            {type === 'success' ? <SuccessIcon /> : <ErrorIcon />}
          </div>
          
          <div className="flex-1 min-w-0 pr-1">
            <h3 className={`text-sm sm:text-base font-bold ${styles.title} mb-1 sm:mb-2`}>
              {title}
            </h3>
            <p className={`text-xs sm:text-sm ${styles.message} mb-2 sm:mb-3 leading-snug`}>
              {message}
            </p>
            
            {paymentId && type === 'success' && (
              <div className="bg-white/60 rounded p-2 border border-green-200/50 mb-2">
                <p className="text-xs text-green-600 font-medium mb-1">Payment ID:</p>
                <p className="text-xs font-mono text-green-800 break-all leading-tight">{paymentId}</p>
              </div>
            )}
            
            {type === 'success' && (
              <div className="flex justify-center text-xs text-green-600">
                <div className="flex items-center space-x-1">
                  <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/>
                  </svg>
                  <span className="text-xs">Email sent</span>
                </div>
              </div>
            )}
          </div>
          
          <button 
            onClick={onClose}
            className="flex-shrink-0 p-1.5 sm:p-2 rounded-full hover:bg-white/60 active:bg-white/80 transition-colors touch-manipulation"
          >
            <CloseIcon />
          </button>
        </div>
        </div>
      </div>
    </div>
  );
};

// Custom CSS for slide-in animation (add this to your global CSS if needed)
const styles = `
  @keyframes slide-in {
    from {
      transform: translateY(-20px) scale(0.95);
      opacity: 0;
    }
    to {
      transform: translateY(0) scale(1);
      opacity: 1;
    }
  }
  
  .animate-slide-in {
    animation: slide-in 0.3s ease-out;
  }
`;

export default PaymentNotification;
